import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { BehaviorSubject, Observable, tap, filter, switchMap, Subscription } from 'rxjs';
import { Moment } from 'moment-timezone';

import { AnalyticsService as AnalyticsSharedService } from '@shared/analytics/services';
import { Metrics, Statistics } from '@shared/analytics/models';
import { ILabels } from '../../analytics.interfaces';

@Component({
  selector: 'tablesready-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
})
export class StatisticsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() fromDate: string;
  @Input() toDate: string;
  @Input() labels: ILabels;
  @Input() size: boolean;
  @Input() statistics: Statistics[];
  @Input() timezone: string;

  @Input() loadTodayStats: () => Observable<Statistics>;
  @Input() simpleDateToMoment: (date: string) => Moment;
  @Input() formatDateToTimestamp: (date: string, text: string) => number;

  metrics: Metrics;
  private readonly subject = new BehaviorSubject<SimpleChanges>(null);
  private readonly subscriptions = new Subscription();

  constructor(private readonly analyticsSharedService: AnalyticsSharedService) {
    this.metrics = this.analyticsSharedService.getEmptyMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subject.next(changes);
  }

  ngOnInit(): void {
    const subjectSub = this.subject
      .pipe(
        filter(changes => {
          if (changes.statistics && changes.statistics.currentValue) {
            return true;
          } else {
            this.updateAnalytics();
            return false;
          }
        }),
        switchMap(() => this.loadTodayStats()),
        tap(stats => {
          this.statistics.push(stats);
          this.updateAnalytics();
        }),
      ).subscribe();

    this.subscriptions.add(subjectSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updateAnalytics(): void {
    const statistics = (this.statistics || []).filter(record =>
      this.simpleDateToMoment(record.date).valueOf() >= this.formatDateToTimestamp(this.fromDate, 'from')
      && this.simpleDateToMoment(record.date).valueOf() <= this.formatDateToTimestamp(this.toDate, 'to'));

    this.loadAnalytics(statistics);
  }

  loadAnalytics(statistics: Statistics[]): void {
    let metrics = this.analyticsSharedService.getEmptyMetrics();
    if (statistics.length === 0) {
      this.metrics = {...metrics};
      return;
    }

    for (let i = 0; i < statistics.length; i++) {
      for (let key in metrics) {
        metrics[key] += statistics[i].data[key];
      }
    }

    this.analyticsSharedService.calcSummaryStats(metrics);

    this.metrics = {...metrics};
  }
}
